import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useT } from '../i18n';

interface Props {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Дополнительные действия слева в footer'е — например, кнопка delete. */
  footer?: ReactNode;
  /** Расширить панель. По умолчанию max-w-lg. */
  size?: 'sm' | 'md' | 'lg';
}

// Общий модал — backdrop + central panel. Закрывается по Escape, клику
// по backdrop и крестику. Внутри панели события click останавливаются,
// чтобы backdrop-handler не срабатывал.
export function Dialog({ open, onClose, title, children, footer, size = 'md' }: Props) {
  const t = useT();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widthCls =
    size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg';

  return (
    <div
      role="presentation"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full ${widthCls} rounded-lg bg-white shadow-pop`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3">{footer}</footer>
        )}
      </div>
    </div>
  );
}
